import { useState } from 'react'
import { useArrived, useTooltip, useWidth } from './Tooltip'

// Twelve readings under a figure: a 2px line in the quiet ink, a wash under it
// at a tenth, and the current reading as a brand dot with a surface ring. A
// reading that was never taken is null, and the line starts at the first one
// that was, so a trend never draws a line through days nobody measured.
//
// Pointing at it names the reading under the pointer; the tile itself carries
// the value and the change for a keyboard or a screen reader.

const H = 36

export function Sparkline({ series, labels }: { series: (number | null)[]; labels?: string[] }) {
  const arrived = useArrived()
  const [ref, w] = useWidth<SVGSVGElement>(200)
  const tip = useTooltip()
  const [hover, setHover] = useState<number | null>(null)

  const n = series.length
  const vals = series.filter((v): v is number => v != null)
  if (n < 2 || vals.length === 0) return <svg ref={ref} className="hc-spark" aria-hidden="true" />
  const max = Math.max(...vals, 1)
  const min = Math.min(...vals, 0)
  const xs = (i: number) => 4 + (i * (w - 8)) / (n - 1)
  const ys = (v: number) => H - 4 - ((v - min) / (max - min || 1)) * (H - 10)

  let d = ''
  let first = -1
  series.forEach((v, i) => {
    if (v == null) return
    d += (first < 0 ? 'M' : 'L') + xs(i).toFixed(1) + ' ' + ys(v).toFixed(1)
    if (first < 0) first = i
  })
  const lastV = series[n - 1] ?? vals[vals.length - 1] ?? 0
  const area = d + 'L' + xs(n - 1).toFixed(1) + ' ' + H + 'L' + xs(first).toFixed(1) + ' ' + H + 'Z'

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    const i = Math.max(0, Math.min(n - 1, Math.round(((e.clientX - r.left - 4) / (r.width - 8)) * (n - 1))))
    setHover(i)
    const v = series[i]
    tip.show(e, labels?.[i] ?? 'Reading ' + (i + 1), [{ label: 'Reading', value: v == null ? 'Not taken' : String(v), color: 'var(--ink-3)' }])
  }

  return (
    <svg
      ref={ref}
      className="hc-spark"
      viewBox={'0 0 ' + w + ' ' + H}
      aria-hidden="true"
      onPointerMove={onMove}
      onPointerLeave={() => {
        setHover(null)
        tip.hide()
      }}
    >
      <path className="area" d={area} />
      <path className="line" d={d} pathLength={1} style={{ strokeDasharray: 1, strokeDashoffset: arrived ? 0 : 1 }} />
      {hover != null && <line className="x" x1={xs(hover)} x2={xs(hover)} y1={2} y2={H} />}
      <circle className="end" cx={xs(n - 1)} cy={ys(lastV)} r={4} />
    </svg>
  )
}
